import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

/**
 * MobileSQLLayout
 *
 * Props:
 *  - problems         : array of problem objects
 *  - selectedProblem  : currently selected problem object
 *  - onSelectProblem  : fn(problem) — called when user taps a problem
 *  - query            : string — current SQL in editor
 *  - onQueryChange    : fn(value) — called on editor change
 *  - onRun            : fn() — called when Run is tapped
 *  - onReset          : fn() — resets editor to starter query
 *  - dbReady          : bool
 *  - results          : { columns, values } | null
 *  - error            : string | null
 *  - validationStatus : "correct" | "almost" | "wrong" | null
 *  - solvedIds        : Set of solved problem ids
 *  - isGuest          : bool
 *  - isPro            : bool
 *  - paywallThreshold : number — problem id above which paywall kicks in for logged-in non-pro users
 *  - guestThreshold   : number — problem id above which guest wall kicks in
 *  - onNavigateSignup : fn() — navigate to signup
 *  - onNavigateLogin  : fn() — navigate to login
 *  - onNavigatePricing: fn() — navigate to pricing
 *  - pageTitle        : string e.g. "SQL Advanced"
 *  - totalProblems    : number
 *  - runCountDisplay  : number
 *  - onPostCommunity  : fn()
 *  - elapsed          : number | null — seconds taken
 */

const TABS = ["Problems", "Editor", "Output"];

const validationConfig = {
  correct: {
    bg: "#f0fdf4", border: "#86efac", icon: "✓", iconBg: "#16a34a",
    title: "Correct!", msg: "Your output matches the expected result.",
    titleColor: "#15803d",
  },
  almost: {
    bg: "#fffbeb", border: "#fcd34d", icon: "~", iconBg: "#d97706",
    title: "Almost there", msg: "Structure looks right but values don't match. Check your filters.",
    titleColor: "#b45309",
  },
  wrong: {
    bg: "#fef2f2", border: "#fca5a5", icon: "✗", iconBg: "#dc2626",
    title: "Not quite", msg: "Result doesn't match. Check columns and re-read the task.",
    titleColor: "#b91c1c",
  },
};

function MobileTableStructure({ tables, schemas }) {
  const [openTable, setOpenTable] = React.useState(null);
  return (
    <div style={{ marginTop: "0.75rem" }}>
      <div style={{ fontSize: "0.65rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px" }}>📋 Table Structure</div>
      {tables.map(table => (
        <div key={table} style={{ marginBottom: "4px", border: "1px solid #e2e8f0", borderRadius: "6px", overflow: "hidden" }}>
          <div
            onClick={() => setOpenTable(openTable === table ? null : table)}
            style={{ padding: "6px 10px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", background: openTable === table ? "#eff6ff" : "#f8fafc" }}
          >
            <span style={{ fontSize: "0.75rem", fontWeight: 700, color: openTable === table ? "#2563eb" : "#0f172a", fontFamily: "monospace" }}>{table}</span>
            <span style={{ fontSize: "0.65rem", color: "#94a3b8", transform: openTable === table ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>▾</span>
          </div>
          {openTable === table && (
            <div style={{ background: "#ffffff", borderTop: "1px solid #e2e8f0" }}>
              {schemas[table].map((col, i) => (
                <div key={col} style={{ padding: "4px 10px", fontSize: "0.72rem", fontFamily: "monospace", color: "#475569", borderBottom: i < schemas[table].length - 1 ? "1px solid #f1f5f9" : "none" }}>
                  {col}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
function ExpandedProblemDetails({ p }) {
  return (
    <>
      {p.expectedColumns && (
        <div style={{ marginTop: "10px", marginBottom: "0.875rem", padding: "12px 14px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "10px" }}>
          <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#2563eb", marginBottom: "8px", letterSpacing: "0.03em" }}>
            REQUIRED OUTPUT COLUMNS
          </div>
          {p.expectedColumns.map(col => (
            <div key={col} style={{ fontSize: "0.78rem", color: "#0f172a", marginBottom: "5px", display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ color: "#2563eb", fontSize: "0.8rem" }}>•</span> {col}
            </div>
          ))}
          <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px dashed #bfdbfe" }} />
          <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.78rem", color: "#2563eb", fontWeight: 700, marginBottom: "6px" }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <line x1="3" y1="9" x2="21" y2="9" />
              <line x1="3" y1="15" x2="21" y2="15" />
              <line x1="9" y1="3" x2="9" y2="21" />
            </svg>
            Expected Rows: {p.expectedRowCount}
          </div>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "6px", fontSize: "0.74rem", color: "#1d4ed8", lineHeight: 1.5 }}>
            <span style={{ fontSize: "0.8rem", flexShrink: 0 }}>ⓘ</span>
            <span>Return ONLY these columns. Additional columns will be marked incorrect.</span>
          </div>
        </div>
      )}

      {p.basics && (
        <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "8px", padding: "0.6rem 0.75rem", marginBottom: "0.875rem" }}>
          <div style={{ fontSize: "0.65rem", fontWeight: 700, color: "#1d4ed8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "3px" }}>
            Real-world scenario
          </div>
          <p style={{ margin: 0, fontSize: "0.76rem", color: "#1e40af", lineHeight: 1.6 }}>{p.basics}</p>
        </div>
      )}

      {p.useCases && p.useCases.length > 0 && (
        <div style={{ marginBottom: "0.875rem" }}>
          <div style={{ fontSize: "0.65rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "5px" }}>
            Common use cases
          </div>
          {p.useCases.map((uc, i) => (
            <div key={i} style={{ display: "flex", gap: "6px", alignItems: "flex-start", marginBottom: "3px" }}>
              <span style={{ color: "#2563eb", fontSize: "0.68rem", marginTop: "2px" }}>→</span>
              <span style={{ fontSize: "0.75rem", color: "#475569", lineHeight: 1.5 }}>{uc}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default function MobileSQLLayout({
  problems,
  selectedProblem,
  onSelectProblem,
  query,
  onQueryChange,
  onRun,
  onReset,
  onShowSolution,
  dbReady,
  results,
  error,
  validationStatus,
  solvedIds,
  isGuest,
  isPro,
  paywallThreshold,
  guestThreshold,
  onNavigateSignup,
  onNavigateLogin,
  onNavigatePricing,
  pageTitle,
  totalProblems,
  runCountDisplay,
  onPostCommunity,
  onShareLinkedIn,
shareOpen,
setShareOpen,
user,
solvedCount,
streak,
firstTry,
elapsed,
ShareModalComponent,
milestones,
expandedMilestone,
setExpandedMilestone,
}) {
  const [activeTab, setActiveTab] = useState(0); // 0=Problems, 1=Editor, 2=Output
  const [expandedId, setExpandedId] = useState(null);
  const navigate = useNavigate();

  // Auto-switch to Output tab when results arrive
  useEffect(() => {
    if (results || error) setActiveTab(2);
  }, [results, error]);

  const handleSelectProblem = (p) => {
    onSelectProblem(p);
    setExpandedId(p.id);
    setActiveTab(1); // jump to editor
  };

  const handleRun = () => {
    if (document.activeElement) document.activeElement.blur();
    onRun();
  };
  const isGuestLocked = isGuest && selectedProblem.id > (guestThreshold ?? 10);
  const isProLocked   = !isGuest && !isPro && selectedProblem.id > (paywallThreshold ?? 30);


  // ─── NAV BAR ────────────────────────────────────────────────────────────────
  const NavBar = () => (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 200,
      background: "#ffffff", borderBottom: "1px solid #e2e8f0",
      padding: "0.75rem 1rem",
      display: "flex", alignItems: "center", justifyContent: "space-between",
    }}>
<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
<button
  onClick={() => navigate("/sql")}
  style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.5rem", color: "#64748b", padding: "0 4px", lineHeight: 1 }}
>
  ←
</button>
<span style={{ fontWeight: 800, fontSize: "1rem", letterSpacing: "-0.3px", color: "#0f172a" }}>
  {pageTitle}
</span>
</div>
      <div style={{
        fontSize: "0.72rem", color: "#16a34a", background: "#f0fdf4",
        border: "1px solid #bbf7d0", borderRadius: "20px", padding: "3px 10px", fontWeight: 600,
      }}>
        ✓ {solvedIds.size}/{totalProblems}
      </div>
    </div>
  );

  // ─── TAB BAR ────────────────────────────────────────────────────────────────
  const TabBar = () => (
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 200,
      background: "#ffffff", borderTop: "1px solid #e2e8f0",
      display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
    }}>
      {TABS.map((tab, i) => (
        <button
          key={tab}
          onClick={() => setActiveTab(i)}
          style={{
            padding: "0.75rem 0",
            border: "none",
            background: activeTab === i ? "#eff6ff" : "#ffffff",
            color: activeTab === i ? "#2563eb" : "#94a3b8",
            fontWeight: activeTab === i ? 700 : 500,
            fontSize: "0.8rem",
            cursor: "pointer",
            borderTop: activeTab === i ? "2px solid #2563eb" : "2px solid transparent",
            transition: "all 0.15s",
            fontFamily: "Inter, -apple-system, sans-serif",
          }}
        >
          {tab === "Output" && (results || error) ? (
            <span>
              {tab}{" "}
              <span style={{
                display: "inline-block", width: "6px", height: "6px",
                borderRadius: "50%",
                background: validationStatus === "correct" ? "#16a34a" : validationStatus === "wrong" ? "#ef4444" : "#f59e0b",
                verticalAlign: "middle", marginLeft: "2px",
              }} />
            </span>
          ) : tab}
        </button>
      ))}
    </div>
  );

  // ─── PROBLEMS TAB ───────────────────────────────────────────────────────────
  const ProblemsTab = () => (
    <div style={{ paddingBottom: "1rem" }}>
      {milestones ? (
        milestones.map((milestone) => {
          const milestoneProblems = problems.filter(
            (p) => p.id >= milestone.range[0] && p.id <= milestone.range[1]
          );
          const solvedInMilestone = milestoneProblems.filter((p) => solvedIds.has(p.id)).length;
          const totalInMilestone = milestoneProblems.length;
          const progressPct = Math.round((solvedInMilestone / totalInMilestone) * 100);
          const isEarned = solvedInMilestone >= totalInMilestone;
          const isOpen = expandedMilestone === milestone.id;
  
          return (
            <div key={milestone.id} style={{ margin: "0.5rem 0.75rem 0.75rem" }}>
              {/* Milestone Header */}
              <div
                onClick={() => setExpandedMilestone(isOpen ? null : milestone.id)}
                style={{
                  background: isEarned ? milestone.bg : "#f8fafc",
                  border: `1.5px solid ${isEarned ? milestone.border : "#e2e8f0"}`,
                  borderRadius: isOpen ? "10px 10px 0 0" : "10px",
                  padding: "0.75rem 0.875rem",
                  cursor: "pointer",
                  display: "flex", alignItems: "center", gap: "10px",
                }}
              >
                <span style={{ fontSize: "1.25rem", lineHeight: 1 }}>{milestone.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "0.82rem", fontWeight: 700, color: isEarned ? milestone.color : "#0f172a" }}>
                    {milestone.label}
                  </div>
                  <div style={{ fontSize: "0.65rem", color: "#94a3b8", marginTop: "2px" }}>
                    Problems {milestone.range[0]}–{milestone.range[1]}
                  </div>
                  <div style={{ marginTop: "6px", height: "3px", background: "#e2e8f0", borderRadius: "2px", overflow: "hidden" }}>
                    <div style={{ width: `${progressPct}%`, height: "100%", background: isEarned ? milestone.color : "#2563eb", borderRadius: "2px" }} />
                  </div>
                  <div style={{ fontSize: "0.62rem", color: "#94a3b8", marginTop: "3px" }}>
                    {solvedInMilestone}/{totalInMilestone} solved
                  </div>
                </div>
                <span style={{ fontSize: "0.7rem", color: isOpen ? "#2563eb" : "#94a3b8", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>▾</span>
              </div>
  
              {/* Problems inside milestone */}
              {isOpen && (
                <div style={{ border: "1.5px solid #e2e8f0", borderTop: "none", borderRadius: "0 0 10px 10px", overflow: "hidden" }}>
                  {milestoneProblems.map((p) => {
                    const isSelected = selectedProblem.id === p.id;
                    const isExpanded = expandedId === p.id;
                    const isSolved = solvedIds.has(p.id);
                    const locked = (isGuest && p.id > (guestThreshold ?? 10)) ||
                                   (!isGuest && !isPro && p.id > (paywallThreshold ?? 30));
                    return (
                      <div key={p.id} style={{ background: "#ffffff", borderBottom: "1px solid #f1f5f9", overflow: "hidden" }}>
                        <div
                          onClick={() => {
                            handleSelectProblem(p);
                            setExpandedId(isExpanded ? null : p.id);
                          }}
                          style={{ padding: "0.75rem 0.875rem", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", background: isSelected ? "#f8faff" : "transparent" }}
                        >
                          <div style={{ width: "22px", height: "22px", borderRadius: "50%", background: isSolved ? "#16a34a" : isSelected ? "#eff6ff" : "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.62rem", fontWeight: 700, color: isSolved ? "#fff" : isSelected ? "#2563eb" : "#94a3b8", flexShrink: 0 }}>
                            {isSolved ? "✓" : locked ? "🔒" : p.id}
                          </div>
                          <span style={{ fontSize: "0.83rem", fontWeight: isSelected ? 700 : 500, color: isSelected ? "#0f172a" : "#334155", flex: 1, lineHeight: 1.35 }}>
                            {p.title}
                          </span>
                          <span style={{ fontSize: "0.7rem", transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", color: "#94a3b8" }}>▾</span>
                        </div>
                        {isExpanded && (
  <div style={{ borderTop: "1px solid #f1f5f9", padding: "0.875rem", background: "#fafbfc" }}>
    <p style={{ margin: "0 0 0.75rem", fontSize: "0.82rem", color: "#0f172a", lineHeight: 1.6 }}>{p.description}</p>
    <ExpandedProblemDetails p={p} />
    {p.hint && (
                              <details>
                                <summary style={{ fontSize: "0.78rem", color: "#2563eb", fontWeight: 600, cursor: "pointer", listStyle: "none" }}>💡 Show hint</summary>
                                <div style={{ marginTop: "6px", padding: "0.5rem 0.625rem", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "6px", fontSize: "0.78rem", color: "#92400e", lineHeight: 1.6, fontFamily: "monospace" }}>
                                  {p.hint}
                                </div>
                              </details>
                              
                            )}
                            {(() => {
  const query = (p.solutionQuery || p.starterQuery || "").toLowerCase();
  const desc = (p.description || "").toLowerCase();
  const combined = query + " " + desc;
  const TABLE_SCHEMAS = {
    customers: ["customer_id","customer_name","email","phone","city","state","country","status","customer_type","lifetime_value"],
    orders: ["order_id","customer_id","order_date","order_status","payment_status","total_amount","currency","delivered_date"],
    order_items: ["order_item_id","order_id","product_id","quantity","unit_price","total_price","item_status"],
    products: ["product_id","product_name","category","subcategory","brand","price","cost_price"],
    payments: ["payment_id","order_id","payment_method","payment_status","amount","currency"],
    delivery_partners: ["delivery_partner_id","partner_name","vehicle_type","city","status","rating"],
    feedback: ["feedback_id","customer_id","order_id","rating","issue_category"],
  };
  const usedTables = Object.keys(TABLE_SCHEMAS).filter(t => combined.includes(t));
  if (usedTables.length === 0) return null;
  return <MobileTableStructure tables={usedTables} schemas={TABLE_SCHEMAS} />;
})()}
                            {!locked && (
                              <button
                                onClick={() => handleSelectProblem(p)}
                                style={{ marginTop: "0.75rem", width: "100%", padding: "8px", borderRadius: "7px", background: "#2563eb", color: "#fff", fontWeight: 700, fontSize: "0.82rem", border: "none", cursor: "pointer" }}
                              >
                                Solve this →
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })
      ) : (
        // Fallback flat list for other category pages
        problems.map((p) => {
          const isSelected = selectedProblem.id === p.id;
          const isExpanded = expandedId === p.id;
          const isSolved = solvedIds.has(p.id);
          const locked = (isGuest && p.id > (guestThreshold ?? 10)) ||
                         (!isGuest && !isPro && p.id > (paywallThreshold ?? 30));
          return (
            <div
              key={p.id}
              style={{ margin: "0.5rem 0.75rem", background: "#ffffff", border: `1.5px solid ${isSelected ? "#2563eb" : "#e2e8f0"}`, borderRadius: "10px", overflow: "hidden", boxShadow: isSelected ? "0 0 0 3px rgba(37,99,235,0.08)" : "none" }}
            >
              <div
                onClick={() => { handleSelectProblem(p); setExpandedId(isExpanded ? null : p.id); }}
                style={{ padding: "0.75rem 0.875rem", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}
              >
                <div style={{ width: "22px", height: "22px", borderRadius: "50%", background: isSolved ? "#16a34a" : isSelected ? "#eff6ff" : "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.62rem", fontWeight: 700, color: isSolved ? "#fff" : isSelected ? "#2563eb" : "#94a3b8", flexShrink: 0 }}>
                  {isSolved ? "✓" : locked ? "🔒" : p.id}
                </div>
                <span style={{ fontSize: "0.83rem", fontWeight: isSelected ? 700 : 500, color: isSelected ? "#0f172a" : "#334155", flex: 1, lineHeight: 1.35 }}>{p.title}</span>
                <span style={{ fontSize: "0.7rem", transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", color: "#94a3b8" }}>▾</span>
              </div>
              {isExpanded && (
  <div style={{ borderTop: "1px solid #f1f5f9", padding: "0.875rem", background: "#fafbfc" }}>
    <p style={{ margin: "0 0 0.75rem", fontSize: "0.82rem", color: "#0f172a", lineHeight: 1.6 }}>{p.description}</p>
    {p.explanation && <p style={{ margin: "0 0 0.75rem", fontSize: "0.78rem", color: "#475569", lineHeight: 1.65 }}>{p.explanation}</p>}
    <ExpandedProblemDetails p={p} />
    {p.hint && (
                    <details>
                      <summary style={{ fontSize: "0.78rem", color: "#2563eb", fontWeight: 600, cursor: "pointer", listStyle: "none" }}>💡 Show hint</summary>
                      <div style={{ marginTop: "6px", padding: "0.5rem 0.625rem", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "6px", fontSize: "0.78rem", color: "#92400e", lineHeight: 1.6, fontFamily: "monospace" }}>{p.hint}</div>
                    </details>
                  )}
                  {!locked && (
                    <button onClick={() => handleSelectProblem(p)} style={{ marginTop: "0.75rem", width: "100%", padding: "8px", borderRadius: "7px", background: "#2563eb", color: "#fff", fontWeight: 700, fontSize: "0.82rem", border: "none", cursor: "pointer" }}>
                      Solve this →
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
      <div style={{ height: "1rem" }} />
    </div>
  );

  // ─── VALIDATION BANNER ──────────────────────────────────────────────────────
  const ValidationBanner = () => {
    if (!validationStatus) return null;
    const c = validationConfig[validationStatus];
    return (
      <div style={{
        background: c.bg, border: `1px solid ${c.border}`,
        borderRadius: "10px", padding: "0.75rem", marginBottom: "0.75rem",
        display: "flex", gap: "10px", alignItems: "flex-start",
      }}>
        <div style={{
          width: "22px", height: "22px", borderRadius: "50%",
          background: c.iconBg, color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "0.7rem", fontWeight: 700, flexShrink: 0,
        }}>
          {c.icon}
        </div>
        <div>
          <div style={{ fontSize: "0.82rem", fontWeight: 700, color: c.titleColor }}>{c.title}</div>
          <div style={{ fontSize: "0.76rem", color: "#475569", marginTop: "2px" }}>{c.msg}</div>

          {validationStatus === "correct" && onPostCommunity && (
  <div style={{ display: "flex", gap: "6px", marginTop: "0.5rem", flexWrap: "wrap" }}>
    <button onClick={onPostCommunity} style={{ fontSize: "0.76rem", color: "#2563eb", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "6px", padding: "4px 10px", cursor: "pointer", fontWeight: 600 }}>🎉 Share to Community</button>
    <button onClick={() => setShareOpen(true)} style={{ fontSize: "0.76rem", color: "#0a66c2", background: "#e8f0fe", border: "1px solid #b0c4f7", borderRadius: "6px", padding: "4px 10px", cursor: "pointer", fontWeight: 600 }}>🔗 Share on LinkedIn</button>
  </div>
)}
        </div>
      </div>
    );
  }
  // ─── OUTPUT TAB ─────────────────────────────────────────────────────────────
  const OutputTab = () => (
    <div style={{ padding: "0.75rem" }}>
      <ValidationBanner />

      <div style={{ border: "1.5px solid #e2e8f0", borderRadius: "10px", overflow: "hidden" }}>
        <div style={{
          background: "#f8fafc", padding: "0.5rem 0.875rem",
          borderBottom: "1px solid #e2e8f0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span style={{ fontSize: "0.68rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Output
          </span>
          {results && (
            <span style={{ fontSize: "0.7rem", color: "#94a3b8" }}>
              {results.values.length} row{results.values.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        <div style={{ minHeight: "120px", padding: "0.875rem", background: "#ffffff" }}>
          {!results && !error && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100px", color: "#94a3b8", fontSize: "0.82rem" }}>
              Run your query to see results
            </div>
          )}
          {error && (
            <div style={{
              color: "#ef4444", fontSize: "0.8rem", fontFamily: "monospace",
              lineHeight: 1.6, background: "#fef2f2", border: "1px solid #fca5a5",
              borderRadius: "8px", padding: "0.75rem",
            }}>
              {error}
            </div>
          )}
          {results && (
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <table style={{ borderCollapse: "collapse", fontSize: "0.78rem", width: "100%" }}>
                <thead>
                  <tr>
                    {results.columns.map((col) => (
                      <th key={col} style={{
                        textAlign: "left", padding: "6px 10px",
                        color: "#64748b", fontWeight: 600,
                        borderBottom: "2px solid #e2e8f0",
                        whiteSpace: "nowrap", fontSize: "0.72rem",
                        textTransform: "uppercase", letterSpacing: "0.03em",
                      }}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {results.values.map((row, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? "#ffffff" : "#f8fafc" }}>
                      {row.map((cell, j) => (
                        <td key={j} style={{
                          padding: "6px 10px", color: "#0f172a",
                          borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap",
                        }}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Re-run button from output tab */}
      <button
        onClick={() => { handleRun(); }}
        disabled={!dbReady}
        style={{
          marginTop: "0.75rem", width: "100%", padding: "10px",
          borderRadius: "8px", background: dbReady ? "#2563eb" : "#94a3b8",
          color: "#fff", fontWeight: 700, fontSize: "0.85rem",
          border: "none", cursor: dbReady ? "pointer" : "not-allowed",
        }}
      >
        {dbReady ? "▶ Run Again" : "Loading…"}
      </button>
      <div style={{ height: "1rem" }} />
    </div>
  );

  // ─── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div style={{
      background: "#ffffff",
      minHeight: "100vh",
      fontFamily: "Inter, -apple-system, sans-serif",
      color: "#0f172a",
      paddingTop: "56px",   // below fixed nav
      paddingBottom: "56px", // above fixed tab bar
      overflowY: "auto",
    }}>
      <NavBar />

      {activeTab === 0 && <ProblemsTab />}
      {activeTab === 1 && (
        isGuestLocked ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem" }}>
            <div style={{ textAlign: "center", maxWidth: "320px" }}>
              <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>🔒</div>
              <h3 style={{ fontSize: "1.1rem", fontWeight: 800, color: "#0f172a", margin: "0 0 0.5rem" }}>Sign in to unlock</h3>
              <p style={{ fontSize: "0.85rem", color: "#64748b", lineHeight: 1.7, marginBottom: "1.5rem" }}>You've explored the first {guestThreshold ?? 10} problems. Sign up free to continue.</p>
              <button onClick={onNavigateSignup} style={{ width: "100%", padding: "11px", borderRadius: "8px", background: "#2563eb", color: "#fff", fontWeight: 700, fontSize: "0.88rem", border: "none", cursor: "pointer", marginBottom: "8px" }}>Sign Up Free →</button>
              <button onClick={onNavigateLogin} style={{ width: "100%", padding: "11px", borderRadius: "8px", background: "#ffffff", color: "#2563eb", fontWeight: 600, fontSize: "0.88rem", border: "1.5px solid #bfdbfe", cursor: "pointer" }}>Already have an account? Sign in</button>
            </div>
          </div>
        ) : isProLocked ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem" }}>
            <div style={{ textAlign: "center", maxWidth: "320px" }}>
              <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>🔒</div>
              <h3 style={{ fontSize: "1.1rem", fontWeight: 800, color: "#0f172a", margin: "0 0 0.5rem" }}>Pro problem</h3>
              <p style={{ fontSize: "0.85rem", color: "#64748b", lineHeight: 1.7, marginBottom: "1.5rem" }}>Upgrade to Pro to unlock all {totalProblems} problems.</p>
              <button onClick={onNavigatePricing} style={{ width: "100%", padding: "11px", borderRadius: "8px", background: "#2563eb", color: "#fff", fontWeight: 700, fontSize: "0.88rem", border: "none", cursor: "pointer" }}>View Pro Plans →</button>
            </div>
          </div>
        ) : (
          <div style={{ padding: "0.75rem" }}>
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderLeft: "3px solid #2563eb", borderRadius: "0 8px 8px 0", padding: "0.625rem 0.875rem", marginBottom: "0.75rem" }}>
              <div style={{ fontSize: "0.65rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "2px" }}>#{selectedProblem.id} — Task</div>
              <p style={{ margin: 0, fontSize: "0.83rem", color: "#0f172a", lineHeight: 1.55 }}>{selectedProblem.description}</p>
            </div>
            <ExpandedProblemDetails p={selectedProblem} />
            {validationStatus && (() => {
              const c = validationConfig[validationStatus];
              return (
                <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: "10px", padding: "0.75rem", marginBottom: "0.75rem", display: "flex", gap: "10px", alignItems: "flex-start" }}>
                  <div style={{ width: "22px", height: "22px", borderRadius: "50%", background: c.iconBg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", fontWeight: 700, flexShrink: 0 }}>{c.icon}</div>
                  <div>
                    <div style={{ fontSize: "0.82rem", fontWeight: 700, color: c.titleColor }}>{c.title}</div>
                    <div style={{ fontSize: "0.76rem", color: "#475569", marginTop: "2px" }}>{c.msg}</div>
                    {validationStatus === "correct" && onPostCommunity && (
  <div style={{ display: "flex", gap: "6px", marginTop: "0.5rem", flexWrap: "wrap" }}>
    <button onClick={onPostCommunity} style={{ fontSize: "0.76rem", color: "#2563eb", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "6px", padding: "4px 10px", cursor: "pointer", fontWeight: 600 }}>🎉 Share to Community</button>
    <button onClick={() => setShareOpen(true)} style={{ fontSize: "0.76rem", color: "#0a66c2", background: "#e8f0fe", border: "1px solid #b0c4f7", borderRadius: "6px", padding: "4px 10px", cursor: "pointer", fontWeight: 600 }}>🔗 Share on LinkedIn</button>
  </div>
)}
                  </div>
                </div>
              );
            })()}
            <div style={{ border: "1.5px solid #e2e8f0", borderRadius: "10px", overflow: "hidden", marginBottom: "0.75rem" }}>
              <div style={{ background: "#f8fafc", padding: "0.5rem 0.875rem", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "0.68rem", background: "#e2e8f0", color: "#0f172a", padding: "2px 8px", borderRadius: "20px", fontWeight: 700 }}>SQL</span>
                <div style={{ display: "flex", gap: "6px" }}>
                  <button onClick={onReset} style={{ fontSize: "0.72rem", color: "#64748b", background: "transparent", border: "1px solid #e2e8f0", borderRadius: "6px", padding: "4px 8px", cursor: "pointer" }}>Reset</button>
                  <button onClick={handleRun} disabled={!dbReady} style={{ padding: "5px 14px", borderRadius: "6px", background: dbReady ? "#2563eb" : "#94a3b8", color: "#fff", fontWeight: 700, fontSize: "0.78rem", border: "none", cursor: dbReady ? "pointer" : "not-allowed" }}>
                    {dbReady ? "▶ Run" : "Loading…"}
                  </button>
                </div>
                <button
  onClick={onShowSolution}
  style={{ fontSize: "0.72rem", color: "#d97706", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "6px", padding: "4px 8px", cursor: "pointer", fontWeight: 600 }}
>
  💡 Solution
</button>
              </div>
              <textarea
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                spellCheck={false}
                autoCorrect="off"
                autoCapitalize="off"
                autoComplete="off"
                style={{ height: "320px", width: "100%", background: "#1e1e1e", color: "#d4d4d4", fontFamily: "monospace", fontSize: "14px", padding: "12px", border: "none", outline: "none", resize: "none", boxSizing: "border-box", lineHeight: 1.6, display: "block" }}
              />
            </div>
            {runCountDisplay > 2 && validationStatus !== "correct" && (
              <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "8px", padding: "0.625rem 0.875rem", fontSize: "0.78rem", color: "#92400e" }}>
                <strong>Stuck?</strong> Go back to Problems tab and expand the hint.
              </div>
            )}
            <div style={{ height: "1rem" }} />
          </div>
        )
      )}
      {activeTab === 2 && <OutputTab />}

      {ShareModalComponent && shareOpen && (
        <ShareModalComponent
          isOpen={shareOpen}
          onClose={() => setShareOpen(false)}
          problem={{ ...selectedProblem, category: selectedProblem.category || "SQL" }}
          user={user}
          solvedCount={solvedCount}
          streak={streak}
          firstTry={firstTry}
          timeTaken={elapsed}
        />
      )}
      <TabBar />
    </div>
  );
}